import React from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Checkbox } from '../../../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { CharitableGivingOptions } from '../../../../../api/useGoalsQuery';
import { AssetBucketingSelect } from '../../../../../components/AssetBucketingSelect/AssetBucketingSelect';
import { CharitableGoalDataDTO } from '../../../CharitableGiving.meta';
import { useFormUtils } from '../../../hooks/useFormUtils';
import { FundAccount } from '../../FundAccount/FundAccount';
import { KindOfProperty } from '../../KindOfProperty/KindOfProperty';
import { CharityName } from '../../CharityName/CharityName';

interface Props {
  options: CharitableGivingOptions;
  clientId: string;
  hasAssetBucketing: boolean;
}

export const OutrightGiftsToCharity = ({ options, clientId, hasAssetBucketing }: Props) => {
  const { formState, control, register } = useFormContext<CharitableGoalDataDTO>();

  const { isRecurring, maximizeDonations, yearOptions } = useFormUtils({ options });

  const { donorOptions } = options;

  return (
    <>
      <KindOfProperty options={options} />

      <CharityName options={options} />

      <Controller
        control={control}
        name="donor"
        render={({ field: { value: formValue, onChange, ...rest } }) => (
          <Select
            {...rest}
            isPortal
            label="Donor"
            value={donorOptions.find(({ value }) => value === formValue)}
            onChange={opt => onChange(opt?.value)}
            options={donorOptions}
            formVariant={formState.errors.donor ? 'error' : undefined}
            helperText={formState.errors.donor?.message}
          />
        )}
      />

      <Box
        display="flex"
        flex={1}
        alignItems="start"
        justifyContent="space-between"
        css={`
          gap: 16px;
        `}
      >
        <Box width="100%">
          <Controller
            control={control}
            name="donationStartYear"
            render={({ field: { value, onChange, ref, disabled } }) => (
              <Select
                isPortal
                innerRef={ref}
                label={isRecurring ? 'Start Year' : 'Donation Year'}
                value={yearOptions.find(opt => opt.value === value)}
                options={yearOptions}
                onChange={opt => onChange(opt?.value)}
                formVariant={formState.errors.donationStartYear ? 'error' : undefined}
                helperText={formState.errors.donationStartYear?.message}
                disabled={disabled}
              />
            )}
          />
        </Box>

        {isRecurring && (
          <Box width="100%">
            <Controller
              control={control}
              name="donationEndYear"
              render={({ field: { value, onChange, ref, disabled } }) => (
                <Select
                  isPortal
                  innerRef={ref}
                  label="End Year"
                  value={yearOptions.find(opt => opt.value === value)}
                  options={yearOptions}
                  onChange={opt => onChange(opt?.value)}
                  formVariant={formState.errors.donationEndYear ? 'error' : undefined}
                  helperText={formState.errors.donationEndYear?.message}
                  disabled={disabled}
                />
              )}
            />
          </Box>
        )}
      </Box>

      <Checkbox {...register('isRecurring')}>Recurring</Checkbox>

      <Controller
        control={control}
        name="donationAmount"
        render={({ field: { onChange, value, disabled, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Donation Amount"
            leftIcon="fa fa-dollar-sign"
            innerText="/annually"
            value={value ?? ''}
            allowNegative={false}
            disabled={maximizeDonations === true || disabled}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={formState.errors.donationAmount ? 'error' : undefined}
            helperText={formState.errors.donationAmount?.message}
          />
        )}
      />

      <FundAccount label="Which account(s) will fund this goal?" />

      {hasAssetBucketing && <AssetBucketingSelect clientId={clientId} control={control} />}
    </>
  );
};
